export type Weekday = 'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su';

export const WEEKDAYS: Weekday[] = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

export interface RecurrenceRule {
  freq: 'weekly';
  interval: number;
  byday: Weekday[];
}

/** A schedule event as stored on the backend (create/update responses). */
export interface ScheduleEvent {
  id: string;
  title: string;
  description?: string | null;
  color: string;
  /** ISO timestamp of the (first) occurrence start. */
  starts_at: string;
  /** ISO timestamp of the (first) occurrence end. */
  ends_at: string;
  recurring: boolean;
  recurrence?: RecurrenceRule | null;
  /** ISO date after which a recurring event stops repeating. */
  recurrence_ends_on?: string | null;
  /** ISO dates of occurrences cancelled by staff. */
  excluded_dates: string[];
  max_participants?: number | null;
  staff_only_registration: boolean;
  requires_confirmation: boolean;
  notify_staff_on_registration: boolean;
  location_id: string;
  created_at?: string;
  updated_at?: string;
}

export type RegistrationStatus = 'pending' | 'confirmed';

/** One expanded occurrence of an event, as returned by the schedule index. */
export interface ScheduleOccurrence {
  event_id: string;
  title: string;
  description?: string | null;
  color: string;
  recurring: boolean;
  /** ISO date identifying the occurrence within its event. */
  occurrence_date: string;
  starts_at: string;
  ends_at: string;
  max_participants?: number | null;
  registrations_count: number;
  /** Registrations still awaiting staff confirmation (for the staff badge). */
  pending_count: number;
  /** Whether the current viewer is registered (always false when logged out). */
  registered: boolean;
  /** The viewer's own registration status, or null when not registered. */
  registration_status: RegistrationStatus | null;
  full: boolean;
  cancelled: boolean;
  staff_only_registration: boolean;
  requires_confirmation: boolean;
  notify_staff_on_registration: boolean;
  /** Up to 3 followed attendees for the avatar stack (privacy: followed only). */
  attendees_preview: ScheduleAttendee[];
  recurrence?: RecurrenceRule | null;
  recurrence_ends_on?: string | null;
  /** Event-level anchor start (first occurrence) — used to prefill the edit form. */
  event_starts_at: string;
  event_ends_at: string;
}

export interface ScheduleAttendee {
  id: string;
  username: string;
  slug: string;
  avatar_small_url?: string | null;
  /** Present only for staff who manage the gym. */
  status?: RegistrationStatus;
  /** Present only for staff who manage the gym; enables confirm/remove. */
  registration_id?: string;
}

export interface ScheduleAttendeesResponse {
  total_count: number;
  can_manage: boolean;
  users: ScheduleAttendee[];
}

export interface RegistrationMeta {
  registrations_count: number;
  registered: boolean;
  status: RegistrationStatus;
  full: boolean;
}

export interface BulkRegisterResult {
  registered_count: number;
  skipped_count: number;
}

export interface ScheduleEventPayload {
  title?: string;
  description?: string | null;
  color?: string;
  starts_at?: string;
  ends_at?: string;
  /** null converts a recurring event back to a single one. */
  recurrence?: RecurrenceRule | null;
  recurrence_ends_on?: string | null;
  max_participants?: number | null;
  staff_only_registration?: boolean;
  requires_confirmation?: boolean;
  notify_staff_on_registration?: boolean;
}

export interface ScheduleEventResponse {
  data: {
    id: string;
    type: 'schedule_event';
    attributes: ScheduleEvent;
  };
}

export interface ScheduleOccurrencesResponse {
  data: Array<{
    id: string;
    type: 'schedule_event_occurrence';
    attributes: ScheduleOccurrence;
  }>;
}
